import { useTrialDiscount } from "~/MemberApp/hooks";
import { useCurrentUser } from "./useCurrentUser";

interface UseAuthZ {
  crew: {
    canViewCrew: boolean;
    canViewUpsell: boolean;
  };
  gatherings: {
    canAttend: boolean;
    canRsvp: boolean;
    canViewDetails: boolean;
  };
  library: {
    canFavorite: boolean;
    canViewArticles: boolean;
    canViewPaywalledArticles: boolean;
  };
  membership: {
    canCancel: boolean;
    canChangeBillingInterval: boolean;
    canChangePlan: boolean;
    canExtendPartnerMembership: boolean;
    canPause: boolean;
    canResumePause: boolean;
    canSeePlanSelect: boolean;
  };
  pricing: {
    canSelectPlan: boolean;
  };
  program: {
    canViewPage: boolean;
    canViewUpsell: boolean;
  };
}

const visitorAuthZ: UseAuthZ = {
  crew: {
    canViewCrew: false,
    canViewUpsell: true,
  },
  gatherings: {
    canAttend: false,
    canRsvp: false,
    canViewDetails: false,
  },
  library: {
    canFavorite: false,
    canViewArticles: true,
    canViewPaywalledArticles: false,
  },
  membership: {
    canCancel: false,
    canChangeBillingInterval: false,
    canChangePlan: false,
    canExtendPartnerMembership: false,
    canPause: false,
    canResumePause: false,
    canSeePlanSelect: false,
  },
  pricing: {
    canSelectPlan: true,
  },
  program: {
    canViewPage: false,
    canViewUpsell: true,
  },
};

export const useAuthZ = (): UseAuthZ => {
  const user = useCurrentUser();
  const trialDiscount = useTrialDiscount();

  if (!user) return visitorAuthZ;

  const hasPremiumPlan = user.subscription.plan == "premium";
  const hasPlusPlanNoTrial = user.subscription.plan == "plus" && user.subscription.status !== "trialing";
  const hasPartnerTrialEnded = trialDiscount?.hasEnded;
  const hasPartnerTrialActivated = !!trialDiscount?.activatedAt;
  const hasPartnerTrialMemberExtended = !!trialDiscount?.extendedAt;
  const hasPlusButHasPartnerTrial =
    user.subscription.plan == "plus" &&
    hasPartnerTrialActivated &&
    !hasPartnerTrialEnded;
  const actsLikePremium = hasPremiumPlan || hasPlusButHasPartnerTrial;
  const hasUpcomingStripeDiscount = !!user.subscription.hasUpcomingDiscount;
  const hasOngoingPartnerTrial =
    !!trialDiscount?.activatedAt && !trialDiscount?.hasEnded;
  const hasSupportedBillingInterval =
    user.subscription.billingInterval == "1-month" ||
    user.subscription.billingInterval == "3-months";

  const isTrialing = user.subscription.status === "trialing";
  const canCancel = !user.isLegacy && !user?.subscription?.canceledAt;
  const canPause = !!(
    !user.isLegacy &&
    user.subscription.pause.isEligible &&
    !hasOngoingPartnerTrial &&
    !isTrialing
  );
  const hasPause =
    user.subscription.pause.hasUpcomingPause ||
    user.subscription.status === "paused";
  const canResumePause = hasPause;
  const canChangePlan =
    !user.isLegacy &&
    !hasPause &&
    (hasPremiumPlan || hasPlusPlanNoTrial) &&
    hasSupportedBillingInterval &&
    (!hasPartnerTrialActivated ||
      hasPartnerTrialEnded ||
      hasPartnerTrialMemberExtended);
  const canSeePlanSelect =
    !user.isLegacy && !hasPause && hasSupportedBillingInterval;
  const canChangeBillingInterval = canChangePlan && !hasUpcomingStripeDiscount;
  const canExtendPartnerMembership =
    hasPartnerTrialActivated &&
    !hasPartnerTrialMemberExtended &&
    !hasPartnerTrialEnded;

  return {
    crew: {
      canViewCrew: actsLikePremium && !!user.cohort.id,
      canViewUpsell: !hasPremiumPlan,
    },
    gatherings: {
      canAttend: actsLikePremium && !hasPause,
      canRsvp: actsLikePremium && !hasPause,
      canViewDetails: actsLikePremium && !hasPause,
    },
    library: {
      canFavorite: true,
      canViewArticles: true,
      canViewPaywalledArticles: (user.isPaying || user.isEditor) && !hasPause,
    },
    membership: {
      canCancel: canCancel,
      canChangeBillingInterval: canChangeBillingInterval,
      canChangePlan: canChangePlan,
      canExtendPartnerMembership: canExtendPartnerMembership,
      canPause: canPause,
      canResumePause: canResumePause,
      canSeePlanSelect: canSeePlanSelect,
    },
    pricing: {
      canSelectPlan: false,
    },
    program: {
      canViewPage: true,
      canViewUpsell: !hasPremiumPlan,
    },
  };
};
